import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { defineStore } from 'pinia';
import { LoadingStateModel } from '@/models';
import { SCAN_PAGE_SIZE, SCAN_PAGE_SIZE_MAX, type PerfusionScanModel } from '@/models/perfusionScan';
import PerfusionScanService from '@/services/perfusionScanService';
import { createRequestToken } from '@/stores/requestToken';

type PerfusionScanStoreShape = {
  scans: Ref<PerfusionScanModel[]>;
  total: Ref<number>;
  /**
   * When this client received the newest read of the first page, in ms by its own clock. A live
   * scan's row judges whether it is still running against it; the live scan's own times come from
   * the phone that ran it, so neither side is the server's clock.
   */
  readAt: Ref<number>;
  /** Whether the server has no older page of the case's scans to send. */
  allLoaded: ComputedRef<boolean>;
  scansLoader: Ref<LoadingStateModel>;
  olderScansLoader: Ref<LoadingStateModel>;
  getScans: (pumpCenterId: string, perfusionId: string) => Promise<void>;
  loadOlderScans: (pumpCenterId: string, perfusionId: string) => Promise<void>;
  getScan: (pumpCenterId: string, perfusionId: string, scanId: string) => Promise<PerfusionScanModel>;
  deleteScan: (pumpCenterId: string, perfusionId: string, scanId: string) => Promise<void>;
  clearScans: () => void;
};

/** A page the server sent, less the scans deleted here since it was asked for, and its total to match. */
const withoutDeleted = (fetched: { scans: PerfusionScanModel[]; total: number }, deleted: ReadonlySet<string>) => {
  const scans = fetched.scans.filter(scan => !deleted.has(scan.id));
  return { scans, total: Math.max(fetched.total - (fetched.scans.length - scans.length), 0) };
};

/** `scans` followed by each of `older` it doesn't already hold, so a page that overlaps adds nothing twice. */
const appendPage = (scans: PerfusionScanModel[], older: PerfusionScanModel[]) => {
  const held = new Set(scans.map(scan => scan.id));
  return [...scans, ...older.filter(scan => !held.has(scan.id))];
};

export const usePerfusionScanStore = defineStore('perfusionScan', (): PerfusionScanStoreShape => {
  const activeCaseId = ref<string | null>(null);
  const scans = ref<PerfusionScanModel[]>([]);
  const total = ref(0);
  const readAt = ref(0);
  const scansLoader = ref(new LoadingStateModel({ isLoading: false }));
  const olderScansLoader = ref(new LoadingStateModel({ isLoading: false }));
  const scansRequest = createRequestToken();
  const olderScansRequest = createRequestToken();
  /**
   * The active case's scans deleted here. spdata-api leaves them out of any read made after the
   * delete, but a read already in flight still carries them and would put them back.
   */
  let deletedIds = new Set<string>();

  const allLoaded = computed(() => scans.value.length >= total.value);

  const getScans = async (pumpCenterId: string, perfusionId: string) => {
    if (activeCaseId.value !== perfusionId) {
      activeCaseId.value = perfusionId;
      // A case change is the one event allowed to empty the panel (`CONTEXT.md`, Refresh in place).
      // Left standing, the previous case's scans read as this one's for the length of the fetch.
      scans.value = [];
      total.value = 0;
      deletedIds = new Set();
    }
    // An older page asked for against the list this read replaces would land at the wrong offset.
    olderScansRequest.supersede();
    olderScansLoader.value.setState({ isLoading: false });
    // Re-read every page the panel has loaded, so a refresh never takes older scans off the screen.
    // One read sends at most `SCAN_PAGE_SIZE_MAX`, so past that the re-read takes several. A scan
    // deleted elsewhere between two of them shifts the server's offsets and one scan is skipped
    // until the next refresh: tolerated, since a refresh is the reader asking again anyway.
    const wanted = Math.max(scans.value.length, SCAN_PAGE_SIZE);
    const token = scansRequest.claim();
    scansLoader.value.startLoading();
    try {
      let read: PerfusionScanModel[] = [];
      let readTotal = 0;
      for (let offset = 0; offset < wanted; offset += SCAN_PAGE_SIZE_MAX) {
        const limit = Math.min(wanted - offset, SCAN_PAGE_SIZE_MAX);
        const fetched = withoutDeleted(
          await PerfusionScanService.getScans(pumpCenterId, perfusionId, limit, offset),
          deletedIds
        );
        if (!scansRequest.holds(token)) return;
        read = appendPage(read, fetched.scans);
        readTotal = fetched.total;
        if (read.length >= readTotal) break;
      }
      scans.value = read;
      total.value = readTotal;
      readAt.value = Date.now();
    } catch (error) {
      if (!scansRequest.holds(token)) return;
      // The rows stay: the panel draws the failure above the scans the read failed to replace.
      scansLoader.value.setError(error);
    } finally {
      if (scansRequest.holds(token)) {
        scansLoader.value.stopLoadingAndFinish();
      }
    }
  };

  /** The page after the scans loaded so far, appended. A failure leaves the list as it was. */
  const loadOlderScans = async (pumpCenterId: string, perfusionId: string) => {
    if (activeCaseId.value !== perfusionId) return;
    const token = olderScansRequest.claim();
    olderScansLoader.value.startLoading();
    try {
      const fetched = await PerfusionScanService.getScans(
        pumpCenterId,
        perfusionId,
        SCAN_PAGE_SIZE,
        scans.value.length
      );
      if (!olderScansRequest.holds(token)) return;
      const kept = withoutDeleted(fetched, deletedIds);
      scans.value = appendPage(scans.value, kept.scans);
      total.value = kept.total;
    } catch (error) {
      if (!olderScansRequest.holds(token)) return;
      olderScansLoader.value.setError(error);
    } finally {
      if (olderScansRequest.holds(token)) {
        olderScansLoader.value.stopLoadingAndFinish();
      }
    }
  };

  /** One scan as it stands now, put in the place of the listed one so the panel agrees with it. */
  const getScan = async (pumpCenterId: string, perfusionId: string, scanId: string) => {
    const scan = await PerfusionScanService.getScan(pumpCenterId, perfusionId, scanId);
    if (activeCaseId.value === perfusionId) {
      scans.value = scans.value.map(listed => (listed.id === scanId ? scan : listed));
    }
    return scan;
  };

  /** Deletes the scan, which spdata-api archives, and takes it off the list. Throws what the server refused. */
  const deleteScan = async (pumpCenterId: string, perfusionId: string, scanId: string) => {
    await PerfusionScanService.deleteScan(pumpCenterId, perfusionId, scanId);
    if (activeCaseId.value !== perfusionId) return;
    deletedIds.add(scanId);
    const before = scans.value.length;
    scans.value = scans.value.filter(scan => scan.id !== scanId);
    if (scans.value.length < before) {
      total.value = Math.max(total.value - 1, 0);
    }
  };

  const clearScans = () => {
    scansRequest.supersede();
    olderScansRequest.supersede();
    activeCaseId.value = null;
    scans.value = [];
    total.value = 0;
    deletedIds = new Set();
    // A request abandoned by the supersede above skips its own `finally`, so the loader is reset
    // here. Left loading, it strands the panel on a skeleton the next time it opens without a load
    // starting. Reset rather than "finished": nothing completed, the store is simply empty again.
    scansLoader.value.setState({ isLoading: false });
    olderScansLoader.value.setState({ isLoading: false });
  };

  return {
    scans,
    total,
    readAt,
    allLoaded,
    scansLoader,
    olderScansLoader,
    getScans,
    loadOlderScans,
    getScan,
    deleteScan,
    clearScans
  };
});
